import type { SiteLayer } from '../types/site';

type GeoJsonGeometry =
  | { type: 'Point'; coordinates: [number, number] }
  | { type: 'LineString'; coordinates: Array<[number, number]> }
  | { type: 'Polygon'; coordinates: Array<Array<[number, number]>> };

export interface SiteLayersGeoJson {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id: string;
    properties: Record<string, unknown>;
    geometry: GeoJsonGeometry;
  }>;
}

function geometry(layer: SiteLayer): GeoJsonGeometry | null {
  const coordinates = layer.geometry.coordinates.map((p) => [p.lng, p.lat] as [number, number]);
  if (layer.geometry.type === 'point') {
    return coordinates[0] ? { type: 'Point', coordinates: coordinates[0] } : null;
  }
  if (layer.geometry.type === 'line') return { type: 'LineString', coordinates };
  if (coordinates.length < 3) return null;
  const first = coordinates[0]!;
  const last = coordinates.at(-1)!;
  const ring = first[0] === last[0] && first[1] === last[1] ? coordinates : [...coordinates, first];
  const holeRings = (layer.geometry.holes ?? [])
    .map((hole) => hole.map((p) => [p.lng, p.lat] as [number, number]))
    .filter((hole) => hole.length >= 3)
    .map((hole) => {
      const hFirst = hole[0]!;
      const hLast = hole.at(-1)!;
      return hFirst[0] === hLast[0] && hFirst[1] === hLast[1] ? hole : [...hole, hFirst];
    });
  return { type: 'Polygon', coordinates: [ring, ...holeRings] };
}

export function siteLayersToGeoJson(layers: readonly SiteLayer[]): SiteLayersGeoJson {
  return {
    type: 'FeatureCollection',
    features: layers.flatMap((layer) => {
      const g = geometry(layer);
      if (!g) return [];
      return [{
        type: 'Feature' as const,
        id: layer.id,
        properties: {
          name: layer.name,
          category: layer.category,
          description: layer.description ?? null,
          source: layer.source,
          visible: layer.visible,
          ...(layer.attributes ?? {}),
        },
        geometry: g,
      }];
    }),
  };
}

export function downloadSiteLayersGeoJson(layers: readonly SiteLayer[], fileName: string): void {
  const blob = new Blob([JSON.stringify(siteLayersToGeoJson(layers), null, 2)], { type: 'application/geo+json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName.endsWith('.geojson') ? fileName : `${fileName}.geojson`;
  a.click();
  URL.revokeObjectURL(url);
}
